/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'react',
	'mobx-react',
	'@ba/ba-ui-carbon',
	'prop-types',
	'../../../lib/@waca/core-client/js/core-client/i18n/Formatter',
	'../../../app/nls/StringResources'
], function(
	React,
	MobxReact,
	BA_UI_CARBON,
	PropTypes,
	FormatUtils,
	StringResources
) {

	const getUISpec = (props) => {
		const { item } = props;
		let spec = item;
		if (item && item.editor && item.editor.uiControl) {
			spec = item.editor.uiControl;
		}
		return spec;
	};

	const getSpecByType = (props, type) => {
		const uiControl = getUISpec(props);
		return uiControl[type + 'Options'];
	};

	const BASE_CLASSNAME = 'properties--controls--ToggledCombo';

	/**
	 * @class ToggledCombo
	 * @description ToggledCombo property control class.
	 * Custom control that supports 3 nested sub-controls:
	 * - ToggleButton to enable / disable
	 * - NumberInput to enter the amount
	 * - Dropdown for multiple choice selection
	 * 
	 * @example
	 * {
	 * 		item: {
	 *			editor: {
	 *				uiControl: {
	 *					id: 'prop1', // required field
	 *					label: 'property label', // required field
	 *					readOnly: false,
	 *
	 *					// sub-control options
	 *					checkBoxOptions: {
	 *						name: 'field1'
	 *					},
	 *					dropDownOptions: {
	 *						label: 'Field 2 label',
	 *						name: 'field2',
	 *						options: [
	 *							{
	 *								label: 'Seconds',
	 *								value: 'seconds'
	 *							},
	 *							{
	 *								label: 'Minutes',
	 *								value: 'minutes',
	 *							},
	 *							{
	 *								label: 'Hours',
	 *								value: 'hours'
	 *							}
	 *						]
	 *					},
	 *					inputOptions: {
	 *						label: 'Field 3 label',
	 *						name: 'field3'
	 *					}
	 *				}
	 *			},
	 *			
	 *			setPropertyValue: (value) => { console.log(`property value set to ${value}`); }, // required field
	 *			value: { // required field
	 *				field1: true,
	 *				field2: 'minutes',
	 *				field3: '5'
	 *			}, // values are enabled - 5 minutes
	 *			defaultValue: {
	 *				field1: false,
	 *				field2: 'seconds',
	 *				field3: '2'
	 *			} // default values in case the value sub-member is not provided
	 *		}
	 * }
	 */
	class ToggledCombo extends React.Component {

		render() {
			const { style, className } = this.props;
			const spec = getUISpec(this.props);
			return (
				<div style={style} className={className}>
					<div className={BASE_CLASSNAME + '--toggle'}>
						{this.renderToggleButton(spec)}
					</div>
					<div className={BASE_CLASSNAME + '--options'}>
						{this.renderInput(spec)}
						{this.renderDropdown(spec)}
					</div>
				</div>
			);
		}

		renderToggleButton(spec) {
			const checkboxSpec = getSpecByType(this.props, 'checkBox');
			const checkboxValue = this._getPropertyValue(checkboxSpec);
			const { id, label, readOnly } = spec;

			return (
				<React.Fragment>
					<div className="ToggleButton--label">{label}</div>
					<BA_UI_CARBON.ToggleSmall
						id={id + '--toggle'}
						labelA={null}
						labelB={null}
						onToggle={(value) => {
							this._setPropertyValue(value, 'checkBox');
						}}
						toggled={checkboxValue}
						readOnly={readOnly}
					/>
				</React.Fragment>
			);
		}

		renderDropdown(spec) {
			const checkboxSpec = getSpecByType(this.props, 'checkBox');
			const dropdownSpec = getSpecByType(this.props, 'dropDown');
			const checkboxValue = this._getPropertyValue(checkboxSpec);
			const dropdownValue = this._getPropertyValue(dropdownSpec);
			const { id, readOnly } = spec;
			const items = dropdownSpec.options;
			const isDisabled = readOnly || !checkboxValue;
			const selectedItem = items.find(item => item.value === dropdownValue) || items[0];
			return (
				<BA_UI_CARBON.Dropdown
					id={id + '--dropdown'}
					items={items}
					size='sm'
					titleText={dropdownSpec.label}
					initialSelectedItem={selectedItem && selectedItem.label}
					disabled={isDisabled}
					onChange={(event) => {
						const value = event.selectedItem.value;
						this._setPropertyValue(value, 'dropDown');
					}}
				/>
			);
		}

		renderInput(spec) {
			const { id, readOnly } = spec;
			const checkboxSpec = getSpecByType(this.props, 'checkBox');
			const inputSpec = getSpecByType(this.props, 'input');
			const checkboxValue = this._getPropertyValue(checkboxSpec);
			const inputValue = this._getPropertyValue(inputSpec);
			const isDisabled = readOnly || !checkboxValue;
			const invalidText = StringResources.get('propInputLabel_number_invalid');

			return (
				<BA_UI_CARBON.NumberInput
					id={id + '--input'}
					invalidText={invalidText}
					label={inputSpec.label}
					value={inputValue}
					disabled={isDisabled}
					onChange={(event) => {
						const value = event.imaginaryTarget.value;
						const processedValue = FormatUtils.format(value, {
							type: 'number'
						});
						this._setPropertyValue(processedValue, 'input');
					}}
				/>
			);
		}

		_getPropertyValue(spec) {
			const { item } = this.props;
			const { defaultValue, getPropertyValue } = item;
			const _value = (getPropertyValue() || {})[spec.name];
			if (_value === undefined) {
				return defaultValue[spec.name];
			} else {
				return _value;
			}
		}

		_setPropertyValue(value, type) {
			const { item } = this.props;
			const { getPropertyValue } = item;
			const spec = getSpecByType(this.props, type);
			const valueObj = getPropertyValue();
			if (valueObj && spec.name) {
				valueObj[spec.name] = value;
			}
		}
	}

	ToggledCombo.propTypes = {
		item: PropTypes.shape({
			editor: PropTypes.shape({
				uiControl: PropTypes.shape({
					id: PropTypes.string,
					label: PropTypes.string,
					readOnly: PropTypes.bool
				})
			}),
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			readOnly: PropTypes.bool,
			setPropertyValue: PropTypes.func.isRequired,
			defaultValue: PropTypes.object,
			getPropertyValue: PropTypes.func.isRequired
		}),
		style: PropTypes.object,
		className: PropTypes.string
	};
	ToggledCombo.defaultProps = {};

	return MobxReact.observer(ToggledCombo);
});